import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { WindowComponent } from '@progress/kendo-angular-dialog';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable, Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { SessionSetting } from 'src/app/core/model/session-setting';
import { FhirService } from 'src/app/core/service/fhir.service';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { UserService } from 'src/app/core/service/user.service';
import { ChatListComponent } from '../chat-list/chat-list.component';

@Component({
  standalone: false,
  selector: 'app-chat-window',
  templateUrl: './chat-window.component.html',
  styleUrls: ['./chat-window.component.scss']
})

export class ChatWindowComponent implements OnDestroy
{
  @ViewChild('chatWindow') public chatWindow: WindowComponent;

  @Input() public isVisible = false;

  @ViewChild(ChatListComponent) private chatList?: ChatListComponent;

  public closeConfirmationOpened = false;

  public defaultLeft = 320;
  public defaultTop = 45;
  public defaultHeight = 500;
  public defaultWidth = 900;
  public minimizedWidth = 250;
  public defaultState = 'default';
  public windowHeight: number;
  public isMaximized = false;

  /**
   * Authentication status
   */
  public isLoggedIn: Observable<boolean>;

  /**
   * Messaging subscription
   */
  private messagingSubscription: Subscription;
  private chatAllowed = false;
  private sessionSetting: SessionSetting;

  constructor(
    private authenticationService: AuthenticationService,
    private storageService: LocalStorageService,
    private messengerService: MessengerService,
    private settingService: SettingService,
    private userService: UserService,
    private fhirService: FhirService)
  {
    this.isVisible = false;
    this.chatAllowed = false;
    this.isLoggedIn = this.authenticationService.isLoggedIn();

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.sessionSetting = sessionSetting;
        this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
          next: (message) =>
          {
            if (message && message.type && (message.type === 'open-chat' || message.type === 'role-changed')) 
            {
              const currentRole = message.type === 'role-changed' ? message.data.selectedRole : this.authenticationService.currentUser.role;
              this.chatAllowed = (!this.fhirEmbedded || this.fhirFullSite) && this.authenticationService.isAuthenticated &&
                this.userService.isRoleInList(currentRole, this.sessionSetting.chatRoles);
              this.isVisible = this.chatAllowed && (message.type === 'open-chat' || (message.type === 'role-changed' && this.isVisible));

              setTimeout(() =>
              {
                // If maximized, we need to set the window dimensions to make sure the chat history is adjusted
                this.isMaximized = this.getState() === 'maximized';
                if (this.isMaximized)
                {
                  this.chatWindow.setDimension('height', this.getHeight());
                }
                // If chat is minimized but user click on the left-menu, we will open
                this.stateChanged((message.type === 'open-chat' && this.getState() === 'minimized') ? 'default' : this.getState());
                this.adjustWindowPositions();
              });
            }
          }
        });
      }
    });
  }

  public get fhirEmbedded(): boolean
  {
    return this.fhirService.embedded;
  }

  public get fhirFullSite(): boolean
  {
    return this.fhirService.fullSite;
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  public onWindowClose(): void
  {
    if (this.chatList?.hasUnsavedComposeDrafts())
    {
      this.closeConfirmationOpened = true;
      return;
    }

    this.hideWindow();
  }

  public cancelCloseWindow(): void
  {
    this.closeConfirmationOpened = false;
  }

  public confirmCloseWindow(): void
  {
    this.closeConfirmationOpened = false;
    this.hideWindow();
  }

  /**
   * Window state. When state is minimized or default, the window position is stored in the localStorage
   * 
   * @param newState New state 
   */
  public stateChanged(newState: string): void
  {
    this.storageService.store('chat-state', newState);
    if (this.isVisible)
    {
      this.isMaximized = newState === 'maximized';
      if (newState === 'minimized') 
      {
        this.chatWindow.setOffset('top', this.getTopMinimized());
        this.chatWindow.setOffset('left', this.getLeftMinimized());
        this.chatWindow.setDimension('width', this.minimizedWidth);
      }
      else if (newState === 'maximized') 
      {
        this.heightChanged(this.chatWindow.height);
      }
      else if (newState === 'default')
      {
        this.chatWindow.setOffset('top', this.getTop());
        this.chatWindow.setOffset('left', this.getLeft());
        this.chatWindow.setDimension('height', this.getHeight());
        this.chatWindow.setDimension('width', this.getWidth());
      }
    }
  }

  /**
   * Window end drag. This store the top and left position when minimized. In case that user don't like the default minimized position.
   */
  public dragEnd(): void
  {
    if (this.getState() === 'minimized') 
    {
      this.storageService.store('chat-top-minimized', this.chatWindow.top);
      this.storageService.store('chat-left-minimized', this.chatWindow.left);
    }
  }

  /**
   * Window top position changed.
   * 
   * @param position New position
   */
  public topChanged(position: number): void 
  {
    if (this.getState() !== 'minimized')
    {
      this.storageService.store('chat-top', position);
    }
  }

  /**
   * Window left position changed.
   * 
   * @param position New position
   */
  public leftChanged(position: number): void 
  {
    if (this.getState() !== 'minimized')
    {
      this.storageService.store('chat-left', position);
    }
  }

  /**
   * Window height size changed.
   * 
   * @param size New size
   */
  public heightChanged(size: number): void 
  {
    this.storageService.store(this.isMaximized ? 'chat-height-maximized' : 'chat-height', size);
    this.windowHeight = size;
  }

  /**
   * Window width size changed.
   * 
   * @param size New size
   */
  public widthChanged(size: number): void 
  {
    this.storageService.store('chat-width', size);
  }

  /**
   * Return the top position from storage or default
   * 
   * @returns Number
   */
  public getTop(): number 
  {
    return this.storageService.retrieve('chat-top') || this.defaultTop;
  }

  /**
   * Return the left position from storage or default
   * 
   * @returns Number
   */
  public getLeft(): number 
  {
    return this.storageService.retrieve('chat-left') || this.defaultLeft;
  }

  /**
   * Return the height size from storage or default
   * 
   * @returns Number
   */
  public getHeight(): number 
  {
    this.windowHeight = this.storageService.retrieve(this.isMaximized ? 'chat-height-maximized' : 'chat-height') || this.defaultHeight;
    return this.windowHeight;
  }

  /**
   * Return the width size from storage or default
   * 
   * @returns Number
   */
  public getWidth(): number 
  {
    return this.storageService.retrieve('chat-width') || this.defaultWidth;
  }

  /**
   * Return the default window state from storage or default
   * 
   * @returns String state (minimized, maximized or default)
   */
  public getState(): string 
  {
    return this.storageService.retrieve('chat-state') || this.defaultState;
  }

  /**
   * Return the top minimized position from storage or default
   * 
   * @returns Number
   */
  public getTopMinimized(): number 
  {
    return this.storageService.retrieve('chat-top-minimized') || this.defaultTop;
  }

  /**
   * Return the left minimized position from storage or default
   * 
   * @returns Number
   */
  public getLeftMinimized(): number 
  {
    return this.storageService.retrieve('chat-left-minimized') || (window.innerWidth - 300);
  }

  private hideWindow(): void
  {
    this.isVisible = false;
  }

  /**
   * When Chat is clicked from the left menu, this method is called to make sure the Chat window is inside the visible boundaries.
   * If the Chat window is outside of the top, left, bottom or right boundaries or not fully visible, it will reset the top and left positions to the default
   * positions.
   */
  private adjustWindowPositions(): void
  {
    const chatModalHeight = this.storageService.retrieve(this.isMaximized ? 'chat-height-maximized' : 'chat-height');
    const chatModalWidth = this.storageService.retrieve('chat-width')
    if (((this.getLeft() + chatModalWidth > window.innerWidth) || this.getLeft() < 0) ||
      ((this.getTop() + chatModalHeight > window.innerHeight) || this.getTop() < 0))
    {
      this.storageService.store('chat-top', this.defaultTop);
      this.storageService.store('chat-left', this.defaultLeft);
    }
  }
}
